/**
 * Schema Builder - Generates Zod schemas from FieldDefinition at runtime
 *
 * Converts FieldValidationConfig to Zod validations automatically
 */

import { z, type ZodSchema, type ZodObject, type ZodRawShape } from 'zod'
import type { FieldDefinition, EntityDefinition, InputType, DbType } from '@gzl10/nexus-sdk'

/**
 * Infer db type from input type for fields without db config.
 * DEPRECATED: Prefer using db: { type: 'array', virtual: true } for virtual fields.
 * This fallback exists for backwards compatibility.
 */
function inferTypeFromInput(input: InputType): DbType {
  switch (input) {
    case 'number':
    case 'decimal':
      return 'decimal'
    case 'checkbox':
    case 'switch':
      return 'boolean'
    case 'date':
      return 'date'
    case 'datetime':
      return 'datetime'
    case 'json':
      return 'json'
    case 'textarea':
    case 'markdown':
      return 'text'
    case 'multiselect':
    case 'transfer':
      return 'array' // Virtual field for multi-select/transfer (array of IDs)
    default:
      return 'string'
  }
}

/**
 * Build Zod schema for a single field based on its definition
 */
function buildFieldSchema(field: FieldDefinition): ZodSchema {
  const { db, validation } = field
  let schema: ZodSchema

  // Use db.type for validation. For virtual fields, use db: { type: 'array', virtual: true }
  // Fallback to inferTypeFromInput for backwards compatibility with fields without db config
  const dbType = db?.type ?? inferTypeFromInput(field.input)

  // Determine base type from db.type
  switch (dbType) {
    case 'string':
    case 'text':
      schema = z.string()

      // Apply string validations
      if (validation?.min !== undefined) {
        schema = (schema as z.ZodString).min(validation.min)
      }
      if (validation?.max !== undefined) {
        schema = (schema as z.ZodString).max(validation.max)
      }
      if (db?.size && !validation?.max) {
        // Use db.size as max length if no explicit max
        schema = (schema as z.ZodString).max(db.size)
      }

      // Auto-validate based on input type (unless custom pattern overrides)
      if (!validation?.pattern) {
        if (field.input === 'email') {
          schema = (schema as z.ZodString).email()
        } else if (field.input === 'url') {
          schema = (schema as z.ZodString).url()
        } else if (field.input === 'uuid') {
          schema = (schema as z.ZodString).uuid()
        } else if (field.input === 'slug') {
          schema = (schema as z.ZodString).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
        }
      }

      if (validation?.pattern) {
        schema = (schema as z.ZodString).regex(new RegExp(validation.pattern))
      }
      if (validation?.enum) {
        schema = z.enum(validation.enum as [string, ...string[]])
      } else if (field.options?.static?.length) {
        // Auto-validate select fields against their static options
        const allowed = field.options.static.map(o => o.value)
        schema = z.enum(allowed as [string, ...string[]])
      }
      break

    case 'integer':
      schema = z.number().int()
      if (validation?.min !== undefined) {
        schema = (schema as z.ZodNumber).min(validation.min)
      }
      if (validation?.max !== undefined) {
        schema = (schema as z.ZodNumber).max(validation.max)
      }
      break

    case 'decimal':
      schema = z.number()
      if (validation?.min !== undefined) {
        schema = (schema as z.ZodNumber).min(validation.min)
      }
      if (validation?.max !== undefined) {
        schema = (schema as z.ZodNumber).max(validation.max)
      }
      break

    case 'boolean':
      // Coerce numeric/string values to boolean (SQLite stores as 0/1)
      schema = z.preprocess((val) => {
        if (val === 0 || val === '0' || val === 'false') return false
        if (val === 1 || val === '1' || val === 'true') return true
        return val
      }, z.boolean())
      break

    case 'date':
    case 'datetime':
      // Accept string (ISO) or Date
      schema = z.union([z.string(), z.date()])
      break

    case 'json':
      schema = z.unknown()
      break

    case 'uuid':
      schema = z.string().uuid()
      break

    case 'array':
      // For multiselect inputs - array of string IDs
      schema = z.array(z.string())
      break

    default:
      schema = z.unknown()
  }

  // Determine if field is required:
  // - field.required: true → required
  // - db.nullable: false (without default) → required
  // - Otherwise → optional/nullable
  const hasDefault = db?.default !== undefined || field.defaultValue !== undefined
  // Infer nullable from required if not explicitly set
  const isNullable = db?.nullable ?? (field.required === true ? false : true)
  const isDbRequired = !isNullable && !hasDefault
  const isRequired = field.required === true || isDbRequired

  if (!isRequired) {
    // Field is optional
    if (isNullable) {
      schema = schema.nullable().optional()
    } else {
      schema = schema.optional()
    }
  }
  // If isRequired, schema remains as-is (not optional)

  return schema
}

/**
 * Fields to exclude from create/update schemas
 */
const AUTO_FIELDS = ['id', 'created_at', 'updated_at', 'created_by', 'updated_by']

/**
 * Build Zod schema for entity create operation
 *
 * - Excludes auto-generated fields (id, timestamps, audit) from required validation
 * - Applies required validation
 * - Uses passthrough() to allow custom fields (like custom id)
 */
export function buildCreateSchema(definition: EntityDefinition): ZodObject<ZodRawShape> {
  const shape: ZodRawShape = {}

  if (!('fields' in definition) || !definition.fields) {
    return z.object(shape).passthrough()
  }

  for (const [name, field] of Object.entries(definition.fields)) {
    // Skip auto fields - they're optional and handled by service
    if (AUTO_FIELDS.includes(name)) continue

    shape[name] = buildFieldSchema(field)
  }

  // passthrough allows custom fields like id to be passed to service
  return z.object(shape).passthrough()
}

/**
 * Build Zod schema for entity update operation
 *
 * - Excludes auto-generated fields
 * - All fields are optional (partial update)
 * - Uses passthrough() to allow custom fields
 */
export function buildUpdateSchema(definition: EntityDefinition): ZodObject<ZodRawShape> {
  const shape: ZodRawShape = {}

  if (!('fields' in definition) || !definition.fields) {
    return z.object(shape).passthrough()
  }

  for (const [name, field] of Object.entries(definition.fields)) {
    // Skip auto fields
    if (AUTO_FIELDS.includes(name)) continue

    // Make all fields optional for update
    let schema = buildFieldSchema(field)
    if (!schema.isOptional()) {
      schema = schema.optional()
    }

    shape[name] = schema
  }

  return z.object(shape).passthrough()
}

/**
 * Cache for compiled schemas (per entity definition)
 */
const schemaCache = new WeakMap<EntityDefinition, {
  create: ZodObject<ZodRawShape>
  update: ZodObject<ZodRawShape>
}>()

/**
 * Get cached schemas for an entity definition
 */
export function getSchemas(definition: EntityDefinition): {
  create: ZodObject<ZodRawShape>
  update: ZodObject<ZodRawShape>
} {
  let cached = schemaCache.get(definition)

  if (!cached) {
    cached = {
      create: buildCreateSchema(definition),
      update: buildUpdateSchema(definition)
    }
    schemaCache.set(definition, cached)
  }

  return cached
}
